import { PropsWithChildren, useEffect, useState } from 'react';
import { DebounceInput } from 'react-debounce-input';
import { useTranslation } from 'react-i18next';
import { CancelOutlined, CheckCircleOutlineOutlined, Medication } from '@mui/icons-material';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import cn from 'classnames';

import useDrugClasses from '@store/hooks/useDrugClasses';
import handleGetBannedMedications from '@utils/handleGetBannedMedications';

import {
  IDietarySupplementsItem,
  IMedicationsItem,
  ISearchSelectBoxProps,
} from './SearchSelectBox.interface';

import './style.scss';

const highlightSearchTerm = (text: string, searchTerm: string): JSX.Element => {
  const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
  return (
    <span className="search-select-box__med-title-text">
      {parts.map((part, index) =>
        part.toLowerCase() === searchTerm.toLowerCase() ? (
          <strong key={index}>{part}</strong>
        ) : (
          part
        ),
      )}
    </span>
  );
};
function SearchSelectBox<T extends IDietarySupplementsItem & IMedicationsItem>({
  className,
  title,
  showTitle = true,
  isMedicationSearch = true,
  selected,
  items,
  isLoading,
  searchValue,
  onSelect,
  onSearch,
  onLoadMore,
  noOptionsText,
  hasInOutCompBan,
}: PropsWithChildren<ISearchSelectBoxProps<T>>) {
  const { t } = useTranslation();
  const drugClasses = useDrugClasses();
  const [isSelectOpen, setIsSelectOpen] = useState<boolean>(false);

  const [listboxNode, setListboxNode] = useState<(EventTarget & Element) | null>(null);
  const [listboxNodeScrollTop, setListboxNodeScrollTop] = useState<number>(0);

  useEffect(() => {
    if (listboxNode) {
      listboxNode.scrollTop = listboxNodeScrollTop;
    }
  }, [items]);

  return (
    <Box
      className={cn('search-select-box', { 'search-select-box_no-frame': !showTitle }, className)}
    >
      {showTitle && (
        <Typography variant="h5" className="search-select-box__title">
          {title}
        </Typography>
      )}

      <Autocomplete
        id="search-select-box"
        disablePortal
        clearOnBlur={false}
        open={isSelectOpen && searchValue.length > 2}
        onOpen={(): void => setIsSelectOpen(true)}
        onClose={(): void => setIsSelectOpen(false)}
        onChange={(event, value) => onSelect(event, value)}
        loading={isLoading}
        isOptionEqualToValue={(item, value): boolean => item.name === value.name}
        filterOptions={() => items}
        getOptionLabel={item => item.name}
        options={items}
        value={selected}
        classes={{
          noOptions: 'search-select-box__no-options',
        }}
        noOptionsText={noOptionsText || t('common.noResultsFound')}
        renderOption={(props, option) => {
          const bannedMeds = handleGetBannedMedications(option.drugClassIds || [], drugClasses);
          const { synonyms } = option;
          return (
            <Box component="li" {...props} key={option.id}>
              <div className="search-select-box__li-container">
                <div className="search-select-box__li-title">
                  <span className="search-select-box__med-title">
                    {!isMedicationSearch && !!option.isMedication && (
                      <Box component="img" src="/images/img-prescription-grey.svg" />
                    )}
                    {highlightSearchTerm(option.name, searchValue)}
                  </span>
                  <Box className="search-select-box__med-info">
                    {!!bannedMeds.length && (
                      <span className="search-select-box__banned-med">
                        {bannedMeds[0]}
                        {bannedMeds[1] ? `, ${bannedMeds[1]}` : ''}
                      </span>
                    )}

                    {!hasInOutCompBan &&
                      !!isMedicationSearch &&
                      (option.isAllowed ? (
                        <CheckCircleOutlineOutlined color="primary" />
                      ) : (
                        <CancelOutlined color="error" />
                      ))}
                  </Box>
                </div>
                {!!synonyms?.length && (
                  <div className="search-select-box__med-synonyms">
                    <span>{t('medicationLookup.otherCommonNames')}:</span>
                    <div className="search-select-box__med-synonym-list">
                      {synonyms.map(item => (
                        <div key={item} className="search-select-box__med-synonym">
                          <span>•</span>
                          <span>&nbsp;</span>
                          {highlightSearchTerm(item, searchValue)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Box>
          );
        }}
        renderInput={params => (
          <DebounceInput
            {...params}
            className="search-select-box__input"
            minLength={3}
            debounceTimeout={400}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
              onSearch(event.target.value)
            }
            element={TextField}
            placeholder={title}
            value={searchValue}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <Medication color={selected ? 'primary' : 'disabled'} />
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {isLoading && <CircularProgress color="primary" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        ListboxProps={{
          onScroll: (event: React.SyntheticEvent) => {
            setListboxNode(event.currentTarget);

            const scrollTop = Math.ceil(listboxNode?.scrollTop || 0);
            setListboxNodeScrollTop(scrollTop);

            if (
              !isLoading &&
              listboxNode &&
              scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight
            ) {
              onLoadMore(searchValue);
            }
          },
        }}
      />
    </Box>
  );
}

export default SearchSelectBox;
